'use client'

// React Imports
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

// Dictionary Imports
import { defaultLocaleFor, dictionaries, LOCALES, resolvePersistedLang } from './dictionaries'
import type { Dictionary, Lang } from './dictionaries'
import { isPluralForms, selectPlural } from './plural'

type LanguageContextValue = {
  lang: Lang
  setLang: (lang: Lang) => void
  dictionary: Dictionary
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

const STORAGE_KEY = 'tvirt-lang'

// The cookie mirrors the choice so the Next SERVER already renders the right
// language — no more english flash before hydration (Luca 2026-07-31).
// localStorage stays for the cross-tab `storage` event only (cookies fire none).
export const LANG_COOKIE = 'tvirt-lang'

const persist = (next: Lang) => {
  localStorage.setItem(STORAGE_KEY, next)
  document.cookie = `${LANG_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`
}

// La locale di ripiego quando nulla è persistito e il browser non dice niente
// di servibile. È l'inglese AMERICANO: en-GB è una variante a sé (task #171),
// e un fallback deve essere una scelta esplicita, non «la prima che capita».
const FALLBACK_LANG: Lang = 'en-US'

// Il tag dichiarato dal browser → la locale che serviamo. Tre gradini, dal più
// specifico al più generico:
//   1. tag IDENTICO a una locale registrata, confronto case-insensitive — i
//      browser scrivono 'pt-BR' ma anche 'pt-br', e la forma canonica è la
//      nostra, non la loro;
//   2. stessi LINGUA + SCRIPT ('sr-Cyrl-ME' → 'sr-Cyrl-RS'): serve al serbo,
//      dove lo script distingue due locale vere e proprie e fermarsi al
//      sottotag primario le confonderebbe, servendo la latinica a chi legge
//      in ćirilica;
//   3. la locale BASE della lingua ('es-AR' → 'es-ES', 'de-AT' → 'de-DE').
// Il norvegese è l'unico alias necessario: i browser dichiarano lo STANDARD
// SCRITTO ('nb'/'nb-NO', raramente 'nn'), noi registriamo la macrolingua
// 'no-NO'; senza la riscrittura nessuno dei tre gradini aggancerebbe.
const matchBrowserLang = (declared: string): Lang | undefined => {
  const lower = declared.toLowerCase().replace(/^n[bn](?=$|-)/, 'no')
  const exact = LOCALES.find(locale => locale.toLowerCase() === lower)

  if (exact !== undefined) {
    return exact
  }

  const subtags = lower.split('-')
  const script = subtags.length > 1 && subtags[1].length === 4 ? subtags[1] : undefined

  const byScript = script === undefined
    ? undefined
    : LOCALES.find(locale => locale.toLowerCase().startsWith(`${subtags[0]}-${script}-`))

  return byScript ?? defaultLocaleFor(subtags[0])
}

export const LanguageProvider = ({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) => {
  // initialLang comes from the cookie read server-side (Providers): SSR and
  // first client render agree on the RIGHT language, not on 'en-US'.
  const [lang, setLangState] = useState<Lang>(initialLang ?? FALLBACK_LANG)

  useEffect(() => {
    // localStorage resta SOVRANO al mount: se diverge dal cookie (es. cookie
    // scritto dal rilevamento su un'altra pagina, o scelta fatta quando il
    // cookie non esisteva — il caso che ha steso 64/66 e2e) vince lui e il
    // cookie si riallinea. Senza nessuno dei due si rileva dal browser e si
    // scrive il cookie per il prossimo SSR.
    // MIGRAZIONE dei codici brevi (task #171, tag BCP 47 pieni): chi aveva
    // scelto 'it' o 'srCyrl' prima del passaggio ritrova la SUA lingua, non
    // l'inglese — resolvePersistedLang traduce e qui si RISCRIVE il valore
    // migrato, così la conversione avviene una volta sola. Un valore
    // irriconoscibile (chiave manomessa, lingua ritirata) non è migrabile:
    // cade sul rilevamento dal browser come se non ci fosse nulla.
    const stored = resolvePersistedLang(localStorage.getItem(STORAGE_KEY))

    if (stored !== undefined) {
      setLangState(stored)
      persist(stored)
    } else if (initialLang === undefined) {
      const detected = matchBrowserLang(navigator.language) ?? FALLBACK_LANG

      setLangState(detected)
      persist(detected)
    }

    // Cross-tab sync (Luca 2026-07-24): the `storage` event fires in every
    // OTHER tab when one writes the key — the language change applies live
    // everywhere instead of waiting for a reload.
    const onStorage = (event: StorageEvent) => {
      const written = event.key === STORAGE_KEY ? resolvePersistedLang(event.newValue) : undefined

      if (written !== undefined) {
        setLangState(written)
        document.cookie = `${LANG_COOKIE}=${written}; path=/; max-age=31536000; samesite=lax`
      }
    }

    window.addEventListener('storage', onStorage)

    return () => window.removeEventListener('storage', onStorage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang: (next: Lang) => {
        persist(next)
        setLangState(next)
      },
      dictionary: dictionaries[lang]
    }),
    [lang]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useTranslation = () => {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider')
  }

  const { dictionary, lang } = context

  // Chiavi plurali (task #171): la foglia è un oggetto di categorie CLDR e il
  // count sceglie la forma via Intl.PluralRules sulla locale attiva; {n} e
  // {count} vengono sostituiti col numero. Le foglie stringa ignorano count.
  // Identità stabile (round webdebt): t cambia solo al cambio lingua, così può
  // stare nelle dependency degli effetti senza rieseguirli a ogni render.
  const t = useCallback(
    (key: string, count?: number): string => {
      const value = key.split('.').reduce<unknown>((node, part) => {
        return node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined
      }, dictionary)

      if (typeof value === 'string') {
        return value
      }

      if (isPluralForms(value)) {
        return selectPlural(lang, value, count)
      }

      return key
    },
    [dictionary, lang]
  )

  return { t, lang: context.lang, setLang: context.setLang }
}
